/**
 * @changelog
 *
 * 2026-09-09 (0.1.0) (jhoward)
 *   - Add src\helpers\getOfferCalendarEvent.ts to turn a redeemable offer's dates into a calendar event, and to build
 *     both an .ics blob and a Google Calendar link from it.
 */

import type { OpcOffer } from '@/defs/api/OpcOffer';
import { getDateFromEngage } from '@/helpers/getdateFromEngage';
import getPropertyName from '@/helpers/getPropertyName';
import getSelectedPrize from '@/helpers/getSelectedPrize';

export interface OfferCalendarEvent {
  title: string;
  description: string;
  location: string;
  start: Date;
  //Null for an all-day event, which is how a hotel arrival with no time reads.
  end: Date | null;
  isAllDay: boolean;
}

/**
 * The date a guest would actually put in their calendar, in order of how specific it is: the prize they reserved
 * (a concert seating, a tournament heat), then a hotel arrival date, and nothing at all for an offer that is just a
 * window of time - "valid through March" is not an appointment.
 */
export default function getOfferCalendarEvent(offer: OpcOffer): OfferCalendarEvent | null {
  const prize = getSelectedPrize(offer);
  const location = getPropertyName(offer.GUEST_PROPERTY_CODE, '');

  if (prize?.PRIZE_START_TS) {
    const start = getDateFromEngage(prize.PRIZE_START_TS);
    if (start) {
      return {
        title: offer.OFFER_COPY_TITLE || prize.CASINO_PRIZE_NAME,
        description: [prize.CASINO_PRIZE_NAME, offer.OFFER_COPY_SUBTITLE].filter(Boolean).join(' — '),
        location,
        start,
        end: prize.PRIZE_END_TS ? getDateFromEngage(prize.PRIZE_END_TS) : null,
        isAllDay: false,
      };
    }
  }

  if (offer.OFFER_COPY_RESNET_ARRIVAL_DATE) {
    const start =
      getDateFromEngage(offer.OFFER_COPY_RESNET_ARRIVAL_DATE) || parseDateOnly(offer.OFFER_COPY_RESNET_ARRIVAL_DATE);
    if (start) {
      const nights = offer.OFFER_COPY_RESNET_STAY_NIGHTS || 0;
      const end = nights > 0 ? new Date(start.getTime() + nights * 86400000) : null;

      return {
        title: offer.OFFER_COPY_TITLE || 'Hotel stay',
        description: [offer.OFFER_COPY_SUBTITLE, nights > 0 ? nights + (nights === 1 ? ' night' : ' nights') : '']
          .filter(Boolean)
          .join(' — '),
        location,
        start,
        end,
        isAllDay: true,
      };
    }
  }

  return null;
}

//ResNet arrival dates are plain 'YYYY-MM-DD', which getDateFromEngage does not match.
function parseDateOnly(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));

  return isNaN(date.getTime()) ? null : date;
}

function padTwo(value: number): string {
  return value.toString().padStart(2, '0');
}

//Local time, no trailing Z: the event is at the property's wall-clock time, and the parsed Date is already local.
function toCalendarStamp(date: Date, isAllDay: boolean): string {
  const stamp = date.getFullYear() + padTwo(date.getMonth() + 1) + padTwo(date.getDate());

  return isAllDay ? stamp : stamp + 'T' + padTwo(date.getHours()) + padTwo(date.getMinutes()) + '00';
}

//RFC 5545: escape the delimiters, and fold anything over 75 octets.
function escapeIcsText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

function foldIcsLine(line: string): string {
  if (line.length <= 75) {
    return line;
  }

  const parts = [line.substring(0, 75)];
  let rest = line.substring(75);
  while (rest.length > 74) {
    parts.push(' ' + rest.substring(0, 74));
    rest = rest.substring(74);
  }
  parts.push(' ' + rest);

  return parts.join('\r\n');
}

export function getIcsForOffer(offer: OpcOffer): string | null {
  const event = getOfferCalendarEvent(offer);
  if (!event) {
    return null;
  }

  const dateSuffix = event.isAllDay ? ';VALUE=DATE' : '';
  const end = event.end || new Date(event.start.getTime() + (event.isAllDay ? 86400000 : 7200000));

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Peppermill//Online Players Club//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    'UID:' + offer.CASINO_OFFER_DOMAIN + '-' + offer.CASINO_OFFER_CODE + '-' + offer.CASINO_OFFER_INVITE_ID + '@opc',
    'DTSTAMP:' + toCalendarStamp(new Date(), false) + 'Z',
    'DTSTART' + dateSuffix + ':' + toCalendarStamp(event.start, event.isAllDay),
    'DTEND' + dateSuffix + ':' + toCalendarStamp(end, event.isAllDay),
    'SUMMARY:' + escapeIcsText(event.title),
    'DESCRIPTION:' + escapeIcsText(event.description),
    'LOCATION:' + escapeIcsText(event.location),
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(foldIcsLine).join('\r\n');
}

export function getGoogleCalendarUrl(offer: OpcOffer): string | null {
  const event = getOfferCalendarEvent(offer);
  if (!event) {
    return null;
  }

  const end = event.end || new Date(event.start.getTime() + (event.isAllDay ? 86400000 : 7200000));
  const parameters = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    details: event.description,
    location: event.location,
    dates: toCalendarStamp(event.start, event.isAllDay) + '/' + toCalendarStamp(end, event.isAllDay),
  });

  return 'https://calendar.google.com/calendar/render?' + parameters.toString();
}
